import { useRef, useState } from "react";
import { Modal, Btn } from "@/components/Kit";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Trash2, Plus, FileText } from "lucide-react";

const PDF_MODULES = ["quotes", "remisiones", "invoices", "work-orders"];

export function ImportDialog({ open, onClose, module, title, onDone }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [manualFolios, setManualFolios] = useState({});
  const [previews, setPreviews] = useState(null);
  const [previewErrors, setPreviewErrors] = useState([]);

  const supportsPdf = PDF_MODULES.includes(module);
  const isWorkOrder = module === "work-orders";
  const isPdf = (f) => /\.pdf$/i.test(f.name);
  const needsFolio = (msg) => /manualmente/i.test(msg || "");
  const folioErrors = (result?.errors || []).filter((e) => needsFolio(e.message));

  const reset = () => {
    setFiles([]);
    setResult(null);
    setLoading(false);
    setManualFolios({});
    setPreviews(null);
    setPreviewErrors([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => {
    reset();
    onClose();
  };

  const downloadTemplate = () => {
    window.open(`/api/import/template/${module}`, "_blank");
  };

  const submit = async () => {
    if (files.length === 0) return toast.error("Selecciona uno o más archivos");
    const pdfs = files.filter(isPdf);
    const sheets = files.filter((f) => !isPdf(f));
    if (pdfs.length && sheets.length) {
      return toast.error("No mezcles PDF con Excel/CSV. Súbelos por separado.");
    }
    if (pdfs.length && !supportsPdf) {
      return toast.error("Este módulo no admite importación por PDF.");
    }

    // Órdenes de Trabajo por PDF: primero previsualizar para revisar/corregir antes de guardar.
    if (pdfs.length && isWorkOrder) {
      setLoading(true);
      setResult(null);
      setPreviews(null);
      try {
        const fd = new FormData();
        pdfs.forEach((f) => fd.append("files", f));
        const { data } = await api.post(`/import/work-orders/pdf?preview=true`, fd);
        setPreviews(data.previews || []);
        setPreviewErrors(data.errors || []);
        if ((data.previews || []).length === 0) {
          toast.error("No se pudo leer ningún PDF para revisar");
        } else {
          toast.success(`${data.previews.length} orden(es) lista(s) para revisar`);
        }
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Error al leer los PDF");
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      let data;
      if (pdfs.length) {
        const fd = new FormData();
        pdfs.forEach((f) => fd.append("files", f));
        ({ data } = await api.post(`/import/${module}/pdf`, fd));
      } else {
        const fd = new FormData();
        fd.append("file", sheets[0]);
        ({ data } = await api.post(`/import/${module}`, fd));
      }
      applyResult(data);
      if (data.created > 0) toast.success(`${data.created} documento(s) importado(s)`);
      if (data.created === 0 && data.errors?.length) toast.error("No se importó ningún documento");
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al importar");
    } finally {
      setLoading(false);
    }
  };

  const label = files.length === 0
    ? "Haz clic para seleccionar archivos"
    : files.length === 1 ? files[0].name : `${files.length} archivos seleccionados`;

  const applyResult = (data) => {
    setResult(data);
    const hints = {};
    (data.errors || []).forEach((e) => {
      if (needsFolio(e.message) && e.folio_hint) hints[e.row] = e.folio_hint;
    });
    if (Object.keys(hints).length) setManualFolios((prev) => ({ ...hints, ...prev }));
  };

  const retryWithFolios = async () => {
    const toRetry = files.filter(
      (f) => folioErrors.some((e) => e.row === f.name) && (manualFolios[f.name] || "").trim()
    );
    if (toRetry.length === 0) return toast.error("Captura el folio de al menos un archivo");
    const map = {};
    toRetry.forEach((f) => { map[f.name] = manualFolios[f.name].trim(); });
    setLoading(true);
    try {
      const fd = new FormData();
      toRetry.forEach((f) => fd.append("files", f));
      fd.append("folios", JSON.stringify(map));
      const { data } = await api.post(`/import/${module}/pdf`, fd);
      applyResult(data);
      if (data.created > 0) toast.success(`${data.created} documento(s) importado(s)`);
      if (data.created === 0 && data.errors?.length) toast.error("No se importó ningún documento");
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al reintentar");
    } finally {
      setLoading(false);
    }
  };

  // ---- Órdenes de Trabajo: helpers de edición de borradores ----
  const updateDraft = (i, patch) =>
    setPreviews((p) => p.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const updateItem = (i, j, patch) =>
    setPreviews((p) => p.map((d, idx) => (idx === i
      ? { ...d, items: d.items.map((it, jdx) => (jdx === j ? { ...it, ...patch } : it)) }
      : d)));
  const addItem = (i) =>
    setPreviews((p) => p.map((d, idx) => (idx === i
      ? { ...d, items: [...(d.items || []), { description: "", quantity: 1, unit_price: 0 }] }
      : d)));
  const removeItem = (i, j) =>
    setPreviews((p) => p.map((d, idx) => (idx === i
      ? { ...d, items: (d.items || []).filter((_, jdx) => jdx !== j) }
      : d)));
  const removeDraft = (i) => setPreviews((p) => p.filter((_, idx) => idx !== i));

  const commitDrafts = async () => {
    if (!previews || previews.length === 0) return;
    for (const d of previews) {
      if (!(d.number || "").trim()) return toast.error("Captura el folio (OT-) de todas las órdenes");
      if (!(d.title || "").trim()) return toast.error("Captura el título de todas las órdenes");
      if (!(d.items || []).some((it) => (it.description || "").trim()))
        return toast.error("Cada orden requiere al menos un concepto");
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/import/work-orders/commit`, { drafts: previews });
      setResult(data);
      setPreviews(null);
      if (data.created > 0) toast.success(`${data.created} orden(es) creada(s)`);
      if (data.created === 0 && data.errors?.length) toast.error("No se guardó ninguna orden");
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-[#E3E8EE] px-2.5 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]";
  const showPreview = Array.isArray(previews) && previews.length > 0;

  return (
    <Modal open={open} onClose={close} title={title} xl={showPreview} wide={!showPreview}>
      <div className="space-y-5">
        {showPreview ? (
          <div className="space-y-5" data-testid="wo-preview-section">
            <div className="border border-[#465B7F]/40 bg-[#E9EEF3] p-4">
              <div className="font-mono-data text-sm text-[#16202B] font-semibold">
                Revisa y corrige los datos antes de guardar
              </div>
              <p className="font-mono-data text-xs text-[#5B6672] mt-1">
                La IA leyó {previews.length} PDF. Verifica el <b>folio (OT-)</b>, el <b>cliente</b>, el <b>título</b> y la <b>remisión relacionada</b>; corrige lo que haga falta antes de procesar. La remisión indicada se vinculará automáticamente a la OT si existe en el sistema.
              </p>
            </div>

            {previewErrors.length > 0 && (
              <div className="border border-[#ED1F26]/40 p-3 space-y-1" data-testid="wo-preview-errors">
                {previewErrors.map((e, idx) => (
                  <div key={idx} className="font-mono-data text-xs text-[#ED1F26]">
                    <AlertTriangle size={13} className="inline mr-1" />{e.row}: {e.message}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-6 max-h-[55vh] overflow-auto pr-1">
              {previews.map((d, i) => (
                <div key={i} className="border border-[#E3E8EE] p-4 space-y-4" data-testid={`wo-draft-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-2 font-mono-data text-xs text-[#5B6672]">
                      <FileText size={14} className="text-[#465B7F]" /> {d.filename || `Orden ${i + 1}`}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDraft(i)}
                      className="text-[#ED1F26] hover:opacity-70"
                      title="Descartar esta orden"
                      data-testid={`wo-draft-discard-${i}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Folio (OT-) *">
                      <input value={d.number || ""} onChange={(e) => updateDraft(i, { number: e.target.value })}
                        className={inputCls} data-testid={`wo-draft-number-${i}`} />
                    </Field>
                    <Field label="Remisión relacionada">
                      <input value={d.remision_number || ""} onChange={(e) => updateDraft(i, { remision_number: e.target.value })}
                        placeholder="Ej. R-1375" className={inputCls} data-testid={`wo-draft-remision-${i}`} />
                    </Field>
                    <Field label="Cliente">
                      <input value={d.client_name || ""} onChange={(e) => updateDraft(i, { client_name: e.target.value })}
                        className={inputCls} data-testid={`wo-draft-client-${i}`} />
                    </Field>
                    <Field label="Tipo de trabajo">
                      <input value={d.tipo_trabajo || ""} onChange={(e) => updateDraft(i, { tipo_trabajo: e.target.value })}
                        className={inputCls} data-testid={`wo-draft-tipo-${i}`} />
                    </Field>
                    <Field label="Título *">
                      <input value={d.title || ""} onChange={(e) => updateDraft(i, { title: e.target.value })}
                        className={inputCls} data-testid={`wo-draft-title-${i}`} />
                    </Field>
                    <Field label="Responsable">
                      <input value={d.assigned_to || ""} onChange={(e) => updateDraft(i, { assigned_to: e.target.value })}
                        className={inputCls} data-testid={`wo-draft-assigned-${i}`} />
                    </Field>
                    <Field label="Fecha entrega">
                      <input type="date" value={d.due_date || ""} onChange={(e) => updateDraft(i, { due_date: e.target.value })}
                        className={inputCls} data-testid={`wo-draft-due-${i}`} />
                    </Field>
                  </div>

                  <div>
                    <div className="overline mb-1.5">Conceptos</div>
                    <div className="space-y-2">
                      {(d.items || []).map((it, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <textarea value={it.description || ""} onChange={(e) => updateItem(i, j, { description: e.target.value })}
                            rows={1} placeholder="Concepto" className={`${inputCls} flex-1 resize-y`} data-testid={`wo-draft-item-desc-${i}-${j}`} />
                          <input type="number" value={it.quantity} onChange={(e) => updateItem(i, j, { quantity: e.target.value })}
                            className={`${inputCls} w-20`} placeholder="Cant" data-testid={`wo-draft-item-qty-${i}-${j}`} />
                          <input type="number" value={it.unit_price} onChange={(e) => updateItem(i, j, { unit_price: e.target.value })}
                            className={`${inputCls} w-28`} placeholder="Precio" data-testid={`wo-draft-item-price-${i}-${j}`} />
                          <button type="button" onClick={() => removeItem(i, j)} className="text-[#ED1F26] hover:opacity-70 mt-1.5" title="Quitar concepto">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => addItem(i)}
                      className="mt-2 inline-flex items-center gap-1 font-mono-data text-xs text-[#465B7F] hover:underline" data-testid={`wo-draft-add-item-${i}`}>
                      <Plus size={13} /> Agregar concepto
                    </button>
                  </div>

                  <Field label="Descripción / notas">
                    <textarea value={d.description || ""} onChange={(e) => updateDraft(i, { description: e.target.value })}
                      rows={2} className={`${inputCls} resize-y`} data-testid={`wo-draft-description-${i}`} />
                  </Field>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <Btn variant="ghost" type="button" onClick={() => { setPreviews(null); setPreviewErrors([]); }}>Volver</Btn>
              <Btn type="button" onClick={commitDrafts} disabled={loading} data-testid="wo-confirm-save-button">
                <CheckCircle2 size={16} /> {loading ? "Guardando…" : `Guardar ${previews.length} orden(es)`}
              </Btn>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 border border-[#E3E8EE] p-4">
              <div>
                <div className="font-mono-data text-sm text-[#16202B] font-semibold">Formatos aceptados</div>
                <p className="font-mono-data text-xs text-[#5B6672] mt-1">
                  Excel/CSV (con plantilla){supportsPdf ? ", o varios PDF de Holded (lectura con IA)." : "."}
                  {isWorkOrder ? " Los PDF se muestran para revisión antes de guardar." : " Se omiten filas vacías."}
                </p>
              </div>
              <Btn variant="outline" type="button" onClick={downloadTemplate} data-testid="download-template-button">
                <Download size={16} /> Plantilla
              </Btn>
            </div>

            <div
              onClick={() => inputRef.current?.click()}
              className="flex flex-col items-center justify-center border-2 border-dashed border-[#CBD5E0] p-8 cursor-pointer hover:border-[#465B7F] hover:bg-[#E9EEF3] transition-colors"
              data-testid="import-dropzone"
            >
              <FileSpreadsheet size={32} className="text-[#465B7F] mb-3" />
              <div className="font-mono-data text-sm text-[#16202B] text-center">{label}</div>
              <div className="font-mono-data text-xs text-[#5B6672] mt-1">
                {supportsPdf ? "Formatos: .xlsx, .csv, .pdf (múltiple)" : "Formatos: .xlsx, .csv"}
              </div>
              <input
                ref={inputRef}
                type="file"
                multiple={supportsPdf}
                accept={supportsPdf
                  ? ".xlsx,.csv,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                  : ".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"}
                className="hidden"
                data-testid="import-file-input"
                onChange={(e) => { setFiles(Array.from(e.target.files || [])); setResult(null); }}
              />
            </div>

            {result && (
              <div className="border border-[#E3E8EE] p-4 space-y-3" data-testid="import-result">
                <div className="flex items-center gap-4 font-mono-data text-sm">
                  <span className="inline-flex items-center gap-1.5 text-[#3E7C4C]">
                    <CheckCircle2 size={15} /> {result.created} creados
                  </span>
                  {result.linked > 0 && <span className="text-[#465B7F]">{result.linked} vinculados a existentes</span>}
                  {result.skipped > 0 && <span className="text-[#5B6672]">{result.skipped} omitidos</span>}
                  {result.errors?.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-[#ED1F26]">
                      <AlertTriangle size={15} /> {result.errors.length} con error
                    </span>
                  )}
                </div>
                {result.linked_details?.length > 0 && (
                  <div className="max-h-40 overflow-auto border-t border-[#E3E8EE] pt-3 space-y-1" data-testid="import-linked">
                    {result.linked_details.map((e, idx) => (
                      <div key={idx} className="font-mono-data text-xs text-[#465B7F]">
                        {e.row}: {e.message}
                      </div>
                    ))}
                  </div>
                )}
                {result.errors?.length > 0 && (
                  <div className="max-h-48 overflow-auto border-t border-[#E3E8EE] pt-3 space-y-1" data-testid="import-errors">
                    {result.errors.map((e, idx) => (
                      <div key={idx} className="font-mono-data text-xs text-[#ED1F26]">
                        {e.row}: {e.message}
                      </div>
                    ))}
                  </div>
                )}
                {folioErrors.length > 0 && (
                  <div className="border-t border-[#E3E8EE] pt-3 space-y-2" data-testid="manual-folio-section">
                    <div className="font-mono-data text-xs text-[#16202B] font-semibold">
                      Captura el folio manualmente para reintentar:
                    </div>
                    {folioErrors.map((e, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="font-mono-data text-xs text-[#5B6672] flex-1 truncate">{e.row}</span>
                        <input
                          value={manualFolios[e.row] || ""}
                          onChange={(ev) => setManualFolios((m) => ({ ...m, [e.row]: ev.target.value }))}
                          placeholder={module === "invoices" ? "Ej. F-102" : module === "remisiones" ? "Ej. R-045" : "Ej. C-0173"}
                          data-testid={`manual-folio-input-${idx}`}
                          className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-xs w-40 focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
                        />
                      </div>
                    ))}
                    <Btn variant="outline" type="button" onClick={retryWithFolios} disabled={loading} data-testid="retry-folios-button">
                      <Upload size={14} /> {loading ? "Reintentando…" : "Reintentar con folios"}
                    </Btn>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Btn variant="ghost" type="button" onClick={close}>{result ? "Cerrar" : "Cancelar"}</Btn>
              <Btn type="button" onClick={submit} disabled={loading || files.length === 0} data-testid="run-import-button">
                <Upload size={16} /> {loading ? (isWorkOrder ? "Leyendo…" : "Importando…") : (isWorkOrder && files.some(isPdf) ? "Leer y revisar" : "Importar")}
              </Btn>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-1">{label}</span>
      {children}
    </label>
  );
}
